import React, { useState, useEffect } from 'react';

// Safe storage wrapper to handle mobile storage issues
const safeCacheStorage = {
  getItem: (key: string) => {
    try {
      // Try localStorage first
      const localValue = localStorage.getItem(key);
      if (localValue) {
        return localValue;
      }
      
      // Fall back to sessionStorage if localStorage fails or is empty
      try {
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue) {
          // console.log(`[Storage] Retrieved ${key} from sessionStorage fallback`);
          return sessionValue;
        }
      } catch (sessionError) {
        console.error(`[Storage Error] SessionStorage fallback failed:`, sessionError);
      }
      
      return null;
    } catch (e) {
      console.error(`[Storage Error] Failed to get ${key}:`, e);
      
      // Last attempt: try sessionStorage directly
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    }
  },
  
  setItem: (key: string, value: string) => {
    let localSuccess = false;
    let sessionSuccess = false;
    
    // Try localStorage first
    try {
      localStorage.setItem(key, value);
      localSuccess = true;
    } catch (e) {
      console.error(`[Storage Error] localStorage failed for ${key}:`, e);
    }
    
    // Always try to save to sessionStorage as well for redundancy
    try {
      sessionStorage.setItem(key, value);
      sessionSuccess = true;
    } catch (sessionError) {
      console.error(`[Storage Error] sessionStorage failed:`, sessionError);
    }
    
    return localSuccess || sessionSuccess;
  }
};

// Helper function to get the API base URL
const getApiBaseUrl = () => {
  // Always use relative URLs to work with proxy
  return '/api';
};

// Define the type for grid data
interface GridData {
  key: string;
  name: string;
  description: string;
  imdbIDs?: string[];
}

// Fallback data in case the API fails
const fallbackGrids: GridData[] = [
  {
    key: 'sitcoms',
    name: 'Best Sitcoms (Fallback)',
    description: 'A selection of the best sitcoms of all time.',
  },
  {
    key: 'top250',
    name: 'Top 250 IMDB Movies (Fallback)',
    description: 'A sample from the IMDB Top 250 movies.',
  },
  {
    key: 'nolan',
    name: 'All Christopher Nolan Movies (Fallback)',
    description: 'A sample of movies directed by Christopher Nolan.',
  },
];

interface StarterGridSelectorProps {
  onSelect: (grid: GridData) => void;
}

export default function StarterGridSelector({ onSelect }: StarterGridSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [grids, setGrids] = useState<GridData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    async function fetchGrids() {
      try {
        setIsLoading(true);
        
        // Always use the relative '/api' URL to go through the proxy
        const apiUrl = '/api/grids';
        // console.log(`Attempting to fetch from: ${apiUrl} (using proxy)`);
        
        // Now try the actual grids endpoint
        const response = await fetch(apiUrl);
        // console.log('API Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch grids (Status: ${response.status})`);
        }
        
        const data = await response.json();
        // console.log('Received data:', data);
        
        setGrids(data);
        if (!safeCacheStorage.getItem('starterGrids')) {
          safeCacheStorage.setItem('starterGrids', JSON.stringify(data));
        }
        setUsingFallback(false);
      } catch (err) {
        console.error('Error fetching grids:', err);
        
        // Use fallback data if the API fails
        // console.log('Using fallback data');
        setGrids(fallbackGrids);
        setUsingFallback(true);
        
        setError('Unable to connect to database. Using fallback data.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchGrids();
  }, []);

  const handleSelect = async (grid: GridData) => {
    // If using fallback data, we need to handle selection differently
    if (usingFallback) {
      // Create a complete grid with some default IMDb IDs
      const imdbIDs = grid.key === 'sitcoms' 
        ? ['tt1442437', 'tt0108778', 'tt0898266'] 
        : grid.key === 'top250' 
          ? ['tt0111161', 'tt0068646', 'tt0071562'] 
          : ['tt1375666', 'tt0816692', 'tt0468569'];
      
      const completeGrid = { ...grid, imdbIDs };
      onSelect(completeGrid);
      return;
    }
    
    try {
      // Fetch the complete grid with IMDB IDs - using direct '/api' path
      const response = await fetch(`/api/grids/${grid.key}`);
      if (!response.ok) {
        throw new Error('Failed to fetch grid details');
      }
      const completeGrid = await response.json();
      onSelect(completeGrid);
    } catch (err) {
      console.error('Error fetching grid details:', err);
      setError('Failed to load grid details. Using fallback data.');
      
      // Create a fallback complete grid
      const imdbIDs = grid.key === 'sitcoms' 
        ? ['tt1442437', 'tt0108778', 'tt0898266'] 
        : grid.key === 'top250' 
          ? ['tt0111161', 'tt0068646', 'tt0071562'] 
          : ['tt1375666', 'tt0816692', 'tt0468569'];
      
      const fallbackCompleteGrid = { ...grid, imdbIDs };
      onSelect(fallbackCompleteGrid);
    }
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg mb-4">
      
      {isLoading ? (
        <div className="text-center py-4 text-gray-300">Loading starter grids...</div>
      ) : error ? (
        <div>
          <div className="text-orange-400 text-center py-2">{error}</div>
          <div className="space-y-2">
            {grids.slice(0, isExpanded ? grids.length : 3).map((grid) => (
              <div
                key={grid.key}
                className="bg-gray-700 border border-gray-600 p-3 rounded hover:bg-gray-600 cursor-pointer transition-colors"
                onClick={() => handleSelect(grid)}
              >
                <h3 className="font-semibold text-lg text-gray-100">{grid.name}</h3>
                <p className="text-sm text-gray-400">{grid.description}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {grids.slice(0, isExpanded ? grids.length : 3).map((grid) => (
            <div
              key={grid.key}
              className="bg-gray-700 border border-gray-600 p-3 rounded hover:bg-gray-600 cursor-pointer transition-colors"
              onClick={() => handleSelect(grid)}
            >
              <h3 className="font-semibold text-lg text-gray-100">{grid.name}</h3>
              <p className="text-sm text-gray-400">{grid.description}</p>
            </div>
          ))}
        </div>
      )}
      
      {!isLoading && !error && !isExpanded && grids.length > 3 && (
        <button
          className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium"
          onClick={() => setIsExpanded(true)}
        >
          Show {grids.length - 3} more lists available
        </button>
      )}
      
      {!isLoading && !error && isExpanded && grids.length > 3 && (
        <button
          className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium"
          onClick={() => setIsExpanded(false)}
        >
          Show fewer lists
        </button>
      )}
    </div>
  );
} 